import React, {useState, useEffect} from 'react'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Box,
    Text,
    Radio,
    RadioGroup,
    Stack as ChakraStack,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useHistory} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {remCalc} from '../../theme/_utils'
import {CloseIcon} from '../icons'
import {useTokenHelper} from '../../utils/token-helper'

const SubscriptionEditAddressModal = ({
    isOpen,
    onClose,
    currentAddress,
    subscriptionId,
    onSuccess,
}) => {
    const {data: customer} = useCurrentCustomer()
    const [selectedAddress, setSelectedAddress] = useState('current')
    const [shippingAddresses, setShippingAddresses] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState('')
    const appOrigin = useAppOrigin(false)
    const history = useHistory()
    const {locale} = useMultiSite()
    const localeId = locale?.alias
    const currentCountryCode = locale?.id?.split('-')[1]?.toUpperCase()
    const styles = useMultiStyleConfig('Subscriptions')

    const {fetchOMSValidToken} = useTokenHelper()

    useEffect(() => {
        if (customer?.addresses) {
            // Only shipping addresses (not billing) and matching current country
            const shipping = customer.addresses.filter(
                (address) =>
                    !address?.c_isB2CBillingAddress &&
                    !address?.c_isB2BAddress &&
                    address.countryCode?.toUpperCase() === currentCountryCode,
            )
            setShippingAddresses((prev) => {
                const prevIds = prev.map((a) => a.addressId).join(',')
                const nextIds = shipping.map((a) => a.addressId).join(',')
                if (prevIds !== nextIds) {
                    return shipping
                }
                return prev
            })

            // Always preselect the first address (default behaviour)
            if (shipping.length > 0) {
                setSelectedAddress(shipping[0].addressId)
            } else {
                setSelectedAddress('current')
            }
        }
        // Only run when customer.addresses changes
    }, [customer?.addresses])

    // Helper to get the selected shipping address details
    const getSelectedShippingDetails = () => {
        const getAddressNumber = (address1) => {
            if (!address1) return undefined
            const parts = address1.trim().split(' ')
            return parts.length > 1 ? parts[parts.length - 1] : undefined
        }

        const addr = shippingAddresses.find((a) => a.addressId === selectedAddress)
        if (addr) {
            return {
                Shipping__City__s: addr.city,
                Shipping__CountryCode__s: addr.countryCode,
                Shipping__PostalCode__s: addr.postalCode,
                Shipping__StateCode__s: addr.stateCode,
                Shipping__Street__s: addr.address1,
                ShippingAddressNumber__c: getAddressNumber(addr.address1),
            }
        }
        return {}
    }

    // Handle the confirm edit delivery address action
    const handleEditDeliveryAddress = async () => {
        try {
            setIsLoading(true)

            const accessToken = await fetchOMSValidToken()
            const shippingDetails = getSelectedShippingDetails()

            const patchPayload = {
                allOrNone: true,
                compositeRequest: [
                    {
                        method: 'PATCH',
                        url: `/services/data/v59.0/sobjects/Subscription__c/${subscriptionId}`,
                        referenceId: 'refSubscription',
                        body: shippingDetails,
                    },
                ],
            }

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/update-subscription-shipping-address-oms`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(patchPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                setMessage('Shipping address updated successfully')
                onClose()
                if (typeof onSuccess === 'function') {
                    onSuccess()
                }
            } else {
                setMessage('Error during shipping address update: ' + result.error)
            }
        } catch (error) {
            console.error('Error during shipping address update:', error)
            setMessage('Error during shipping address update: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent padding="40px 24px">
                <ModalHeader {...styles.subscriptionEditHeaderContent}>
                    <FormattedMessage
                        id="subscription_edit_address.title"
                        defaultMessage="Shipping address"
                    />
                </ModalHeader>
                <ModalCloseButton {...styles.modalClose} top={remCalc(36)}>
                    <CloseIcon {...styles.modalCloseIcon} />
                </ModalCloseButton>
                <ModalBody {...styles.subscriptionEditBodyContent}>
                    <Text mb={4} {...styles.infoValue}>
                        <FormattedMessage
                            id="subscription_edit_address.desc"
                            defaultMessage="Edit the shipping address. The new shipping address will be active starting from the next order"
                        />
                    </Text>
                    <RadioGroup value={selectedAddress} onChange={setSelectedAddress}>
                        <ChakraStack gap={4}>
                            {/* Customer shipping addresses */}
                            {shippingAddresses
                                .filter(
                                    (addr) =>
                                        `${addr.firstName || ''} ${addr.lastName || ''}`.trim() !==
                                            currentAddress?.name?.trim() ||
                                        addr.street !== currentAddress?.street,
                                )
                                .map((addr) => (
                                    <Box
                                        as="label"
                                        key={addr.addressId}
                                        border="1px solid"
                                        borderColor="border.dark"
                                        borderRadius="md"
                                        p={4}
                                        w="100%"
                                        cursor="pointer"
                                        _checked={{borderColor: 'border.dark'}}
                                    >
                                        <Radio
                                            value={addr.addressId}
                                            mr={4}
                                            {...styles.subscriptionEditRadio}
                                        >
                                            <Text as="span" fontWeight="bold">
                                                <FormattedMessage
                                                    id="subscription_edit_address.use_this"
                                                    defaultMessage="Use this address"
                                                />
                                            </Text>
                                        </Radio>
                                        <Box mt={2}>
                                            <Text fontWeight="bold">{addr.fullName}</Text>
                                            <Text>{addr.phone}</Text>
                                            <Text>{addr.address1}</Text>
                                            <Text>{addr.street}</Text>
                                            <Text>
                                                {addr.city}, {addr.postalCode}
                                            </Text>
                                        </Box>
                                    </Box>
                                ))}
                        </ChakraStack>
                    </RadioGroup>
                    {message && (
                        <Text color="red.500" mt={4}>
                            {message}
                        </Text>
                    )}
                </ModalBody>
                <ModalFooter
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    paddingBottom="0px"
                    paddingX={0}
                >
                    <Button
                        variant="secondary"
                        width="100%"
                        mb={4}
                        onClick={() => history.push(`/${localeId}/account/addresses`)}
                    >
                        <FormattedMessage
                            id="subscription_edit_address.create_new"
                            defaultMessage="Create new shipping address"
                        />
                    </Button>
                    <Button
                        variant="primary"
                        width="100%"
                        onClick={handleEditDeliveryAddress}
                        isLoading={isLoading}
                    >
                        <FormattedMessage
                            id="subscription_edit_address.confirm"
                            defaultMessage="Confirm"
                        />
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

SubscriptionEditAddressModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentAddress: PropTypes.shape({
        name: PropTypes.string,
        phone: PropTypes.string,
        street: PropTypes.string,
        city: PropTypes.string,
        postalCode: PropTypes.string,
        countryCode: PropTypes.string,
        stateCode: PropTypes.string,
        addressNumber: PropTypes.string,
        address1: PropTypes.string,
    }),
    subscriptionId: PropTypes.string,
    onSuccess: PropTypes.func, // add this
    // addresses: PropTypes.array,
    // onSelectAddress: PropTypes.func,
    // onCreateNewAddress: PropTypes.func,
    // onConfirm: PropTypes.func,
}

export default SubscriptionEditAddressModal
